import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TextField, Button, Alert,
  TablePagination, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Grid, Card, CardContent, Avatar, IconButton,
  Menu, MenuItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import API_CONFIG, { getApiUrl } from '../../config/api';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuUser, setMenuUser] = useState(null);

  const navigate = useNavigate();

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.USER.LIST_USERS_WITH_ONBOARDING), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_password: 'Admin@123!!!',
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          order: order === 'asc' ? 'asc' : 'desc'
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setUsers(data.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMenuClick = (event, user) => {
    setAnchorEl(event.currentTarget);
    setMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuUser(null);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setUserDialogOpen(true);
    handleMenuClose();
  };

  const handleEditUser = (user) => {
    // Navigate to user edit page or open edit dialog
    console.log('Edit user:', user);
    handleMenuClose();
  };

  const handleDeleteUser = (user) => {
    // Implement user deletion
    console.log('Delete user:', user);
    handleMenuClose();
  };

  const handleToggleUserStatus = (user) => {
    // Implement user status toggle (active/inactive)
    console.log('Toggle user status:', user);
    handleMenuClose();
  };

  useEffect(() => {
    fetchUsers();
  }, [order, dateFrom, dateTo]);

  // Global search filter
  const filteredUsers = users.filter(user => {
    if (!search) return true;
    const searchStr = search.toLowerCase();
    return Object.values(user).some(val =>
      (val ? String(val).toLowerCase() : '').includes(searchStr)
    );
  });

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const aValue = a[orderBy] || '';
    const bValue = b[orderBy] || '';

    if (order === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  const getUserStatusColor = (user) => {
    // Add logic to determine user status based on your requirements
    return 'success'; // default to success
  };

  const getUserStatusText = (user) => {
    // Add logic to determine user status text
    return 'Active'; // default to active
  };

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Users Management
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label="From Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="To Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              placeholder="Search users..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="contained" onClick={fetchUsers} fullWidth>
              Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'created_at'}
                  direction={orderBy === 'created_at' ? order : 'asc'}
                  onClick={() => handleSort('created_at')}
                >
                  Registered At
                </TableSortLabel>
              </TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Goal</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(user => (
              <TableRow key={user.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      {user.display_name ? user.display_name.charAt(0).toUpperCase() : 'U'}
                    </Avatar>
                    <Typography variant="body2" fontWeight="medium">
                      {user.display_name || 'No name'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <Chip
                    label={user.role || 'User'}
                    size="small"
                    color={user.role === 'admin' ? 'secondary' : 'default'}
                  />
                </TableCell>
                <TableCell>{user.goal || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={getUserStatusText(user)}
                    size="small"
                    color={getUserStatusColor(user)}
                  />
                </TableCell>
                <TableCell>
                  <IconButton onClick={(e) => handleMenuClick(e, user)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={sortedUsers.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* User Details Dialog */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          User Details - {selectedUser?.display_name || selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Basic Information</Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
                      <Typography><strong>Name:</strong> {selectedUser.display_name || 'N/A'}</Typography>
                      <Typography><strong>User ID:</strong> {selectedUser.id}</Typography>
                      <Typography><strong>Registered:</strong> {new Date(selectedUser.created_at).toLocaleString()}</Typography>
                      <Typography><strong>Role:</strong> {selectedUser.role || 'User'}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Onboarding Information</Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                      <Typography><strong>Source:</strong> {selectedUser.source || 'N/A'}</Typography>
                      <Typography><strong>Goal:</strong> {selectedUser.goal || 'N/A'}</Typography>
                      <Typography><strong>Feature:</strong> {selectedUser.feature || 'N/A'}</Typography>
                      <Typography><strong>Referral Code:</strong> {selectedUser.referral_code || 'N/A'}</Typography>
                      <Typography><strong>Referral URL:</strong> {selectedUser.referrer_url || 'N/A'}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* User Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewUser(menuUser)}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleEditUser(menuUser)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleToggleUserStatus(menuUser)}>
          <ListItemIcon>
            <BlockIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Toggle Status</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteUser(menuUser)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UsersManagement;